// frontend/src/components/AdminEvents.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService, supabase } from '../services/supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import AdminHeader from './AdminHeader';
import { formatRelativeTime } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthProvider';

const AdminEvents = () => {
    const { isEventAdmin, assignedEventIds } = useAuth();
    const [events, setEvents] = useState([]);
    const [eventStats, setEventStats] = useState({}); // keyed by event.id → { teams, total, uploaded, lastUpdated }
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'
    const menuRef = useRef(null);

    const [formData, setFormData] = useState({
        code: '',
        name: '',
        shortName: '',
        startDate: '',
        endDate: '',
        location: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchEvents();
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpenId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch per-event stats (teams + photo aggregates) once events have loaded
    useEffect(() => {
        if (!events.length) return;

        let cancelled = false;
        const fetchStats = async () => {
            try {
                // Teams per event_id
                const { data: teamRows } = await supabase
                    .from('teams')
                    .select('event_id');

                const teamCounts = {};
                (teamRows || []).forEach(r => {
                    if (r.event_id) teamCounts[r.event_id] = (teamCounts[r.event_id] || 0) + 1;
                });

                // Photo stats per event — one query per event, mirroring the dashboard's
                // eq('event_name', short_name || code) pattern. Run in parallel.
                // .range bypasses Supabase's default 1000-row cap per event.
                const photoResults = await Promise.all(
                    events.map(async (event) => {
                        const eventName = event.short_name || event.code;
                        if (!eventName) return { id: event.id, total: 0, uploaded: 0, lastUpdated: null };

                        const { data: rows, error } = await supabase
                            .from('event_uploads')
                            .select('upload_status, updated_at')
                            .eq('event_name', eventName)
                            .range(0, 99999);

                        if (error) {
                            console.error(`Stats fetch failed for ${eventName}:`, error);
                            return { id: event.id, total: 0, uploaded: 0, lastUpdated: null };
                        }

                        let total = 0, uploaded = 0, lastUpdated = null;
                        (rows || []).forEach(r => {
                            total++;
                            if (r.upload_status) uploaded++;
                            if (r.updated_at) {
                                const t = new Date(r.updated_at);
                                if (!lastUpdated || t > new Date(lastUpdated)) {
                                    lastUpdated = r.updated_at;
                                }
                            }
                        });

                        return { id: event.id, total, uploaded, lastUpdated };
                    })
                );

                if (cancelled) return;

                const merged = {};
                photoResults.forEach(({ id, total, uploaded, lastUpdated }) => {
                    merged[id] = {
                        teams: teamCounts[id] || 0,
                        total,
                        uploaded,
                        lastUpdated
                    };
                });
                setEventStats(merged);
            } catch (err) {
                // Silent fail — overlay just won't show stats. Don't block the page.
                console.error('Event stats fetch failed:', err);
            }
        };

        fetchStats();
        return () => { cancelled = true; };
    }, [events]);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const { data, error } = await adminService.getAllEvents();

            if (error) {
                toast.error('Failed to load events: ' + error.message);
            } else {
                setEvents(data || []);
            }
        } catch (err) {
            console.error('Fetch events error:', err);
            toast.error('Failed to load events. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    const handleArchiveToggle = async (event) => {
        const newStatus = !event.is_archived;
        const action = newStatus ? 'archive' : 'unarchive';

        if (!window.confirm(`Are you sure you want to ${action} "${event.name}"?`)) {
            return;
        }

        const { error } = await adminService.toggleEventArchiveStatus(event.id, newStatus);

        if (error) {
            toast.error(`Failed to ${action} event`);
        } else {
            toast.success(`Event ${action}d successfully`);
            fetchEvents();
        }
        setMenuOpenId(null);
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setThumbnailFile(file);
            setThumbnailPreview(URL.createObjectURL(file));
        }
    };

    const resetForm = () => {
        setFormData({
            code: '',
            name: '',
            shortName: '',
            startDate: '',
            endDate: '',
            location: ''
        });
        setThumbnailFile(null);
        setThumbnailPreview(null);
        setEditingEvent(null);
    };

    const openEditModal = (event) => {
        setEditingEvent(event);
        setFormData({
            code: event.code,
            name: event.name,
            shortName: event.short_name,
            startDate: event.start_date,
            endDate: event.end_date,
            location: event.location || ''
        });
        setThumbnailPreview(event.thumbnail_url || null);
        setThumbnailFile(null);
        setShowModal(true);
        setMenuOpenId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            let thumbnailUrl = editingEvent?.thumbnail_url || null;

            // Upload thumbnail if new file selected
            if (thumbnailFile) {
                const eventId = editingEvent?.id || 'new_' + Date.now();
                const { url, error: uploadError } = await adminService.uploadEventThumbnail(eventId, thumbnailFile);
                if (uploadError) {
                    toast.error('Failed to upload thumbnail');
                } else {
                    thumbnailUrl = url;
                }
            }

            if (editingEvent) {
                // Update existing event
                const { error } = await adminService.updateEvent(editingEvent.id, {
                    name: formData.name,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    location: formData.location,
                    thumbnailUrl: thumbnailUrl
                });

                if (error) {
                    toast.error('Failed to update event: ' + error.message);
                } else {
                    toast.success('Event updated successfully!');
                    setShowModal(false);
                    resetForm();
                    fetchEvents();
                }
            } else {
                // Create new event
                const { data, error } = await adminService.createEvent({
                    ...formData,
                    thumbnailUrl: thumbnailUrl
                });

                if (error) {
                    toast.error('Failed to create event: ' + error.message);
                } else {
                    // Update thumbnail with real event ID if we uploaded before creation
                    if (thumbnailUrl && data?.id) {
                        await adminService.updateEvent(data.id, { thumbnailUrl });
                    }
                    toast.success('Event created successfully!');
                    setShowModal(false);
                    resetForm();
                    fetchEvents();
                }
            }
        } catch (err) {
            toast.error('An error occurred');
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const formatDateRange = (start, end) => {
        if (!start || !end) return '';
        const startDate = new Date(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const endDate = new Date(end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${startDate} - ${endDate}`;
    };

    // event_admins only ever see events they've been assigned. This is the frontend
    // half of the scoping (write-access is enforced by RLS); every downstream count,
    // tab badge, and card list derives from this scoped set.
    const scopedEvents = isEventAdmin
        ? events.filter(e => assignedEventIds.includes(e.id))
        : events;

    // Events visible after applying tab + search filters. Tab badge counts intentionally
    // remain unfiltered so users see at-a-glance whether their search is hiding things.
    const trimmedSearch = searchQuery.trim().toLowerCase();
    const visibleEvents = scopedEvents.filter(e =>
        (activeTab === 'active' ? !e.is_archived : e.is_archived) &&
        (!trimmedSearch || (e.name || '').toLowerCase().includes(trimmedSearch) || (e.code || '').toLowerCase().includes(trimmedSearch))
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <Toaster position="top-center" />

            <AdminHeader currentPage="events" />

            {/* Page Title Section */}
            <div className="bg-white border-b shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Event Management</h1>
                        <p className="text-gray-500 mt-1">Create, archive and monitor all photo upload events.</p>
                    </div>
                    {/* event_admins cannot create events (no INSERT policy); hide the button. */}
                    {!isEventAdmin && (
                        <div>
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowModal(true);
                                }}
                                className="px-6 py-2.5 bg-mjwc-primary text-white rounded-full hover:bg-mjwc-secondary font-black uppercase tracking-widest text-xs flex items-center gap-2 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                </svg>
                                New Event
                            </button>
                        </div>
                    )}
                </div>
            </div>


            {/* Content */}
            <div className="px-6 py-6">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                ) : scopedEvents.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No events</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {isEventAdmin ? 'No events have been assigned to you yet.' : 'Get started by creating a new event.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Search */}
                        <div className="mb-4 relative max-w-md">
                            <input
                                type="text"
                                placeholder="Search events by name or code..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                            />
                            <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                    aria-label="Clear search"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 mb-6">
                            <button
                                onClick={() => setActiveTab('active')}
                                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'active'
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Active
                                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                                    {scopedEvents.filter(e => !e.is_archived).length}
                                </span>
                            </button>
                            <button
                                onClick={() => setActiveTab('archived')}
                                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'archived'
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Archived
                                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                                    {scopedEvents.filter(e => e.is_archived).length}
                                </span>
                            </button>
                        </div>

                        {/* Event Cards Grid */}
                        {visibleEvents.length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-lg shadow">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                                <h3 className="mt-2 text-sm font-medium text-gray-900">
                                    {trimmedSearch ? 'No matching events' : `No ${activeTab} events`}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {trimmedSearch
                                        ? `No ${activeTab} events match "${searchQuery.trim()}".`
                                        : (activeTab === 'active' ? 'Create a new event to get started.' : 'Events you archive will appear here.')}
                                </p>
                                {trimmedSearch && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                                    >
                                        Clear search
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {visibleEvents.map((event) => (
                                    <div
                                        key={event.id}
                                        className={`group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${event.is_archived ? 'opacity-60' : ''}`}
                                        onClick={() => navigate(`/admin/${event.code}`)}
                                    >
                                        {/* Thumbnail */}
                                        <div className="h-32 bg-gradient-to-br from-indigo-500 to-purple-600 relative">
                                            {event.thumbnail_url ? (
                                                <img
                                                    src={event.thumbnail_url}
                                                    alt={event.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <svg className="w-16 h-16 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                            )}
                                            {/* Status Badge */}
                                            <div className="absolute top-2 left-2">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${event.is_archived
                                                    ? 'bg-gray-100 text-gray-600'
                                                    : 'bg-green-100 text-green-700'
                                                    }`}>
                                                    {event.is_archived ? 'Archived' : 'Active'}
                                                </span>
                                            </div>
                                            {/* 3-dot Menu */}
                                            <div className="absolute top-2 right-2" ref={menuOpenId === event.id ? menuRef : null}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setMenuOpenId(menuOpenId === event.id ? null : event.id);
                                                    }}
                                                    className="p-1.5 bg-white/90 rounded-full hover:bg-white shadow-sm"
                                                >
                                                    <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                                    </svg>
                                                </button>
                                                {menuOpenId === event.id && (
                                                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openEditModal(event);
                                                            }}
                                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                            Edit Details
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleArchiveToggle(event);
                                                            }}
                                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                            </svg>
                                                            {event.is_archived ? 'Unarchive' : 'Archive'}
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/admin/${event.code}`);
                                                            }}
                                                            className="w-full px-4 py-2 text-left text-sm text-indigo-600 hover:bg-indigo-50 flex items-center gap-2"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                            </svg>
                                                            Dashboard
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Hover stats overlay */}
                                            {(() => {
                                                const stats = eventStats[event.id];
                                                if (!stats) return null;
                                                const pct = stats.total > 0 ? Math.round((stats.uploaded / stats.total) * 100) : 0;
                                                return (
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none flex items-end p-3">
                                                        <div className="text-white text-xs space-y-1.5 w-full">
                                                            <div className="flex items-center gap-1.5">
                                                                <svg className="w-3.5 h-3.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                                </svg>
                                                                <span className="font-semibold">{stats.teams}</span>
                                                                <span className="opacity-80">{stats.teams === 1 ? 'team' : 'teams'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5">
                                                                <svg className="w-3.5 h-3.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                </svg>
                                                                <span className="font-semibold">{stats.uploaded}/{stats.total}</span>
                                                                <span className="opacity-80">photos</span>
                                                                {stats.total > 0 && <span className="opacity-60 ml-auto">{pct}%</span>}
                                                            </div>
                                                            {stats.lastUpdated && (
                                                                <div className="flex items-center gap-1.5">
                                                                    <svg className="w-3.5 h-3.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    <span className="opacity-90">Updated {formatRelativeTime(stats.lastUpdated)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        {/* Content */}
                                        <div className="p-4">
                                            <h3 className="font-semibold text-gray-900 truncate">{event.name}</h3>
                                            <p className="text-sm text-gray-500 mt-1">{formatDateRange(event.start_date, event.end_date)}</p>
                                            {event.location && (
                                                <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    {event.location}
                                                </p>
                                            )}
                                            <p className="text-xs text-gray-400 mt-2 font-mono">{event.code}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Create/Edit Modal */}
                        {showModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                                <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                                    <h2 className="text-xl font-bold mb-4">
                                        {editingEvent ? 'Edit Event' : 'Create New Event'}
                                    </h2>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        {/* Thumbnail Upload */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Event Thumbnail</label>
                                            <div className="flex items-center gap-4">
                                                <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                                                    {thumbnailPreview ? (
                                                        <img src={thumbnailPreview} alt="Preview" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <svg className="w-8 h-8 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer text-sm">
                                                        {thumbnailPreview ? 'Change Image' : 'Upload Image'}
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={handleThumbnailChange}
                                                        />
                                                    </label>
                                                    <p className="text-xs text-gray-500 mt-1">Optional. JPG, PNG or WebP</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Code (read-only in edit mode) */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Event Code (URL Safe)</label>
                                            <input
                                                name="code"
                                                required
                                                placeholder="e.g., mjwc2026"
                                                value={formData.code}
                                                onChange={handleChange}
                                                disabled={!!editingEvent}
                                                className={`w-full px-3 py-2 border rounded-md ${editingEvent ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                                                pattern="[a-z0-9-]+"
                                                title="Lowercase letters, numbers, and hyphens only"
                                            />
                                            {editingEvent && (
                                                <p className="text-xs text-amber-600 mt-1">Code cannot be changed after creation</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                            <input
                                                name="name"
                                                required
                                                placeholder="e.g., Men's Junior World Cup 2026"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border rounded-md"
                                            />
                                        </div>

                                        {/* Short Name (read-only in edit mode) */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Short Name</label>
                                            <input
                                                name="shortName"
                                                required
                                                placeholder="e.g., MJWC 2026"
                                                value={formData.shortName}
                                                onChange={handleChange}
                                                disabled={!!editingEvent}
                                                className={`w-full px-3 py-2 border rounded-md ${editingEvent ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                                            />
                                            {editingEvent ? (
                                                <p className="text-xs text-amber-600 mt-1">Short name cannot be changed (linked to player data)</p>
                                            ) : (
                                                <p className="text-xs text-gray-500 mt-1">Used for database/folder mapping</p>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                                <input
                                                    name="startDate"
                                                    type="date"
                                                    required
                                                    value={formData.startDate}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border rounded-md"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                                <input
                                                    name="endDate"
                                                    type="date"
                                                    required
                                                    value={formData.endDate}
                                                    onChange={handleChange}
                                                    className="w-full px-3 py-2 border rounded-md"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                                            <input
                                                name="location"
                                                required
                                                placeholder="e.g., London, UK"
                                                value={formData.location}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border rounded-md"
                                            />
                                        </div>

                                        <div className="flex gap-3 mt-6">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowModal(false);
                                                    resetForm();
                                                }}
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                                disabled={isSaving}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSaving}
                                                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            >
                                                {isSaving && (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                )}
                                                {editingEvent ? 'Save Changes' : 'Create'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminEvents;
